import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTag, FaChevronRight as FaArrow, FaProjectDiagram } from 'react-icons/fa';
import { projectsApi, tagsApi } from '../../../../services/api';
import { SEGMENT_TYPE } from '../../../../constants/radar';
import {
  DescriptionPanel,
  DescriptionContent,
  CollapseButton,
  VerticalTitle,
  CollapsedContainer,
  ExpandedContainer,
  ExpandedHeader,
  CloseButtonContainer,
  ProjectsList,
  ProjectItem,
  LoadingSpinner,
  EmptyMessage,
  TagsList,
  TagItem,
  SectionHeader,
} from './styles';

const Description = ({ data }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagsError, setTagsError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      if (data?.segmentType === SEGMENT_TYPE.LEAF) {
        setIsLoading(true);
        try {
          const projectsData = await projectsApi.getProjectsByLeafId(data.leafSegmentId);
          setProjects(projectsData);
          setError(null);
        } catch (err) {
          setError('Failed to load associated projects');
          setProjects([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setProjects([]);
      }
    };

    const fetchTags = async () => {
      if (data?.segmentType === SEGMENT_TYPE.LEAF) {
        setIsTagsLoading(true);
        try {
          const tagsData = await tagsApi.getTagsByLeafId(data.leafSegmentId);
          setTags(tagsData);
          setTagsError(null);
        } catch (err) {
          setTagsError('Failed to load tags');
          setTags([]);
        } finally {
          setIsTagsLoading(false);
        }
      } else {
        setTags([]);
      }
    };

    fetchProjects();
    fetchTags();
  }, [data]);

  if (!data) return null;

  const renderTags = (tags) => {
    // Filter only directly selected tags
    const selectedTags = tags.filter(tag => tag.isDirectlySelected);
    
    return (
      <TagsList>
        {selectedTags.map(tag => (
          <TagItem 
            key={tag.id}
            $isSelected={true}
          >
            {tag.name}
          </TagItem>
        ))}
      </TagsList>
    );
  };

  return (
    <DescriptionPanel $isCollapsed={isCollapsed}>
      {isCollapsed ? (
        <CollapsedContainer>
          <CollapseButton onClick={() => setIsCollapsed(false)}>
            <FaChevronRight />
          </CollapseButton>
          <VerticalTitle>{data.title}</VerticalTitle>
        </CollapsedContainer>
      ) : (
        <ExpandedContainer>
          <CloseButtonContainer>
            <CollapseButton onClick={() => setIsCollapsed(true)}>
              <FaChevronLeft />
            </CollapseButton>
          </CloseButtonContainer>
          <ExpandedHeader>
            <h2>{data.title}</h2>
          </ExpandedHeader>
          <DescriptionContent>
            <p>{data.description}</p>
            
            {data.segmentType === SEGMENT_TYPE.LEAF && (
              <>
                <SectionHeader>
                  <FaTag /> Tags
                </SectionHeader>
                {isTagsLoading ? (
                  <LoadingSpinner>Loading tags...</LoadingSpinner>
                ) : tagsError ? (
                  <div>{tagsError}</div>
                ) : tags.length > 0 ? (
                  renderTags(tags)
                ) : (
                  <EmptyMessage>No tags assigned</EmptyMessage>
                )}

                <SectionHeader>
                  <FaProjectDiagram /> Associated Projects
                </SectionHeader>
                {isLoading ? (
                  <LoadingSpinner>Loading projects...</LoadingSpinner>
                ) : error ? (
                  <div>{error}</div>
                ) : projects.length > 0 ? (
                  <ProjectsList>
                    {projects.map(project => (
                      <ProjectItem key={project.id}>
                        <span>{project.name}</span>
                        <small>{project.code}</small>
                      </ProjectItem>
                    ))}
                  </ProjectsList>
                ) : (
                  <EmptyMessage>No associated projects</EmptyMessage>
                )}
              </>
            )}
          </DescriptionContent>
        </ExpandedContainer>
      )}
    </DescriptionPanel>
  );
};

export default Description; 
